import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const tableau: Competitor = {
  "id": "tableau",
  "name": "Tableau",
  "group": "bi",
  "website": "https://www.tableau.com",
  "owner": "Salesforce (acquired 2019)",
  "summary": "Long-established visual analytics platform, sold as Tableau Cloud (SaaS), Tableau Server (self-hosted), Tableau Desktop and the newer Agentforce-based Tableau Next. Built for analysts who author highly customized interactive visualizations, often across many data sources, using live connections or in-memory extracts.",
  "bestFor": [
    "Organizations that need advanced, highly customized visualizations and a very large pool of experienced authors to hire from",
    "Companies that must self-host BI on-premises or in their own cloud (Tableau Server)",
    "Salesforce-centric companies that want analytics and AI agents tied into Salesforce and Slack (Tableau Next / Agentforce)",
    "Mixed data estates where some sources are not cloud warehouses and in-memory extracts are useful"
  ],
  "notFor": [
    "Business users who want to work in a spreadsheet-style grid with formulas",
    "Teams that need native write-back or data entry into the warehouse without buying a third-party extension",
    "Teams that want to author entirely in the browser (some features, such as configuring actions and Viz in Tooltip, still require Desktop)",
    "Buyers who want the AI assistant on a standard license (Tableau Agent needs the Tableau+ / Cloud+ edition on Cloud)"
  ],
  "strengths": [
    "Deeper visualization flexibility and polish than Sigma, with many years of chart types, mapping (such as drive-time areas added in 2026) and formatting control",
    "Self-hosted option (Tableau Server, including container builds on Server+); Sigma is SaaS only",
    "In-memory extracts let dashboards run fast and avoid warehouse compute costs on repeated queries; Sigma always queries the warehouse",
    "Published list prices: Standard starts at $15 per user per month, and a free Tableau Desktop edition is available for local files",
    "Much larger community, training ecosystem and talent pool (Tableau Public, user groups, certifications)",
    "Native ties to Salesforce, Slack and Agentforce for teams already on Salesforce",
    "Works with almost any data source, including on-premises databases through Tableau Bridge"
  ],
  "limitations": [
    "No native write-back to the warehouse; data entry needs paid third-party extensions from Tableau Exchange",
    "No spreadsheet-style formula grid for business users",
    "Web authoring lacks some Desktop features, such as configuring actions and Viz in Tooltip, clustering and forecasts",
    "On Cloud, Tableau Agent requires the Tableau+ / Cloud+ edition (contact sales); per-role Creator/Explorer/Viewer prices are no longer shown on the main pricing page",
    "Annual contracts only, with no monthly plans, and at least one Creator license per deployment",
    "Email subscriptions deliver only PNG/PDF snapshots; Slack is for alerts and sharing, not scheduled report delivery",
    "Native git workflows exist only for Tableau Semantics models in Tableau Next (via the DX extension), not for classic workbooks, which rely on revision history",
    "The product line is now split across Cloud, Server, Desktop, Next and the Tableau+ bundle, which makes licensing harder to understand"
  ],
  "pricing": {
    "model": "per user, annual contract (also capacity-based viewer blocks on Cloud and 8-core compute licensing on Server)",
    "publicStartingPrice": "$15/user/month billed annually (Tableau Standard, starting price); Enterprise from $35; Tableau Next from $40/user/month",
    "freeTier": "yes: Tableau Desktop Free Edition (local files, cannot publish or share) and Tableau Public; also a free trial",
    "notes": "Tableau's pricing page shows edition starting prices only: Standard $15, Enterprise $35, Cloud+/Server+/Tableau+ via sales. Third-party licensing advisor Redress Compliance (updated Sept 2026) lists per-role prices of Creator $75 / Explorer $42 / Viewer $15 on Standard and $115 / $70 / $35 on Enterprise, per user per month billed annually. AI (Tableau Agent) on Cloud requires Cloud+ / Tableau+.",
    "sourceUrl": "https://www.tableau.com/pricing/teams-orgs",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "Live connections to Snowflake, BigQuery, Redshift and others, or in-memory extracts; the author chooses.",
      "sourceUrl": "https://www.tableau.com/blog/tableau-online-tips-extracts-live-connections-cloud-data-53351"
    },
    "spreadsheet_interface": {
      "value": "no",
      "note": "Authoring uses visual shelves and calculated fields, not a spreadsheet-style formula grid.",
      "sourceUrl": "https://help.tableau.com/current/online/en-us/server_desktop_web_edit_differences.htm"
    },
    "sql_editor": {
      "value": "partial",
      "note": "Custom SQL data sources, limited to a single SELECT statement that Tableau wraps in its own queries. Not a full SQL workspace.",
      "sourceUrl": "https://help.tableau.com/current/pro/desktop/en-us/customsql.htm",
      "badge": "Custom SQL source only"
    },
    "python_support": {
      "value": "partial",
      "note": "Python runs through TabPy analytics extensions, which you host yourself, called from table calculations. Supported on Cloud and Server.",
      "sourceUrl": "https://www.tableau.com/developer/tools/python-integration-tabpy",
      "badge": "Via TabPy"
    },
    "writeback": {
      "value": "partial",
      "note": "Not native. Available only through paid third-party extensions on Tableau Exchange, such as Write-Back Cloud.",
      "sourceUrl": "https://exchange.tableau.com/products/992",
      "badge": "Third-party extension"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Published data sources and virtual connections; Tableau Semantics models in Tableau Next (Semantic Model Builder GA August 2026).",
      "sourceUrl": "https://www.tableau.com/2026-2-august-features"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Embedding API v3 and connected apps with JWT; per-user or usage-based embedded pricing via sales (per third-party review).",
      "sourceUrl": "https://blog.holistics.io/blog/tableau-embedded-analytics/"
    },
    "ai_assistant": {
      "value": "partial",
      "note": "Tableau Agent is GA in web authoring, but on Cloud it requires the Tableau+ edition. Tableau Agent in Dashboards was still beta in 2026.",
      "sourceUrl": "https://help.tableau.com/current/online/en-us/web_author_einstein.htm",
      "badge": "Tableau+ edition on Cloud"
    },
    "version_control_git": {
      "value": "partial",
      "note": "Workbooks have revision history (restore and notes) but no git. The Tableau Semantics DX extension adds git and CI/CD for Tableau Next semantic models only.",
      "sourceUrl": "https://help.tableau.com/current/pro/desktop/en-us/qs_revision_history.htm"
    },
    "open_source_or_self_host": {
      "value": "yes",
      "note": "Tableau Server is self-hosted on Windows or Linux; not open source.",
      "sourceUrl": "https://www.tableau.com/pricing/teams-orgs"
    },
    "desktop_authoring_required": {
      "value": "partial",
      "note": "Web authoring covers most work, but some features (such as actions, Viz in Tooltip setup, clusters and forecasts) need Tableau Desktop.",
      "sourceUrl": "https://help.tableau.com/current/online/en-us/server_desktop_web_edit_differences.htm"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Email subscriptions with PNG/PDF on a schedule or after extract refresh; Slack app for alerts and sharing.",
      "sourceUrl": "https://help.tableau.com/current/online/en-us/subscribe_user.htm"
    },
    "row_level_security": {
      "value": "yes",
      "note": "User filters, database RLS on live connections, JWT user attributes for embeds; centralized data policies need the Data Management add-on.",
      "sourceUrl": "https://help.tableau.com/current/online/en-gb/rls_options_overview.htm"
    },
    "excel_export": {
      "value": "yes",
      "note": "Crosstab download to .xlsx or CSV, if the author allows it (described by a third-party guide).",
      "sourceUrl": "https://www.thebricks.com/resources/guide-how-to-download-data-from-tableau-dashboard-to-excel"
    }
  },
  "recentChanges": [
    {
      "date": "2025",
      "change": "Tableau Next launched at Tableau Conference 2025: an agentic analytics product built on the Salesforce Agentforce platform.",
      "sourceUrl": "https://atrium.ai/resources/tableau-conference-2026-key-takeaways/"
    },
    {
      "date": "2026-01",
      "change": "January 2026 release: Tableau Agent added to Tableau Server (needs your own OpenAI API keys), dashboard narratives beta, and AI-generated semantic models.",
      "sourceUrl": "https://www.tableau.com/2025-3-january-features"
    },
    {
      "date": "2026-05",
      "change": "Announced an agentic analytics platform with an AI knowledge layer at Tableau Conference 2026 (San Diego). GA planned for later in 2026, with deeper Salesforce integration in early 2027.",
      "sourceUrl": "https://www.pulse.bot/big-data/news/tableau-unveils-agentic-analytics-platform-adding-ai-knowledge-layer-to-bi-suite-55989c3e-f0f5-43ee-915c-a651d1ab1e15/"
    },
    {
      "date": "2026-08",
      "change": "August 2026 release: Semantic Model Builder GA in Tableau Next, Semantics DX extension with git/CI-CD, hosted Tableau MCP announced as coming soon, and Tableau Agent in Dashboards beta.",
      "sourceUrl": "https://www.tableau.com/2026-2-august-features"
    },
    {
      "date": "2026",
      "change": "Pricing page restructured around Standard / Enterprise / Cloud+ / Server+ / Tableau+ editions, with Tableau Next listed at $40 per user per month.",
      "sourceUrl": "https://www.tableau.com/pricing/teams-orgs"
    }
  ],
  "sources": [
    {
      "title": "Tableau pricing (teams and orgs)",
      "url": "https://www.tableau.com/pricing/teams-orgs"
    },
    {
      "title": "Tableau August 2026 release features",
      "url": "https://www.tableau.com/2026-2-august-features"
    },
    {
      "title": "Tableau January 2026 release features",
      "url": "https://www.tableau.com/2025-3-january-features"
    },
    {
      "title": "Tableau help: Tableau Agent",
      "url": "https://help.tableau.com/current/online/en-us/web_author_einstein.htm"
    },
    {
      "title": "Tableau help: Web authoring vs Desktop",
      "url": "https://help.tableau.com/current/online/en-us/server_desktop_web_edit_differences.htm"
    },
    {
      "title": "Tableau help: Custom SQL",
      "url": "https://help.tableau.com/current/pro/desktop/en-us/customsql.htm"
    },
    {
      "title": "Tableau help: Revision history",
      "url": "https://help.tableau.com/current/pro/desktop/en-us/qs_revision_history.htm"
    },
    {
      "title": "Tableau help: RLS options",
      "url": "https://help.tableau.com/current/online/en-gb/rls_options_overview.htm"
    },
    {
      "title": "Tableau help: Subscriptions",
      "url": "https://help.tableau.com/current/online/en-us/subscribe_user.htm"
    },
    {
      "title": "Tableau help: Tableau app for Slack",
      "url": "https://help.tableau.com/current/online/en-us/slack_tableau.htm"
    },
    {
      "title": "Tableau: TabPy Python integration",
      "url": "https://www.tableau.com/developer/tools/python-integration-tabpy"
    },
    {
      "title": "Tableau blog: Extracts vs live connections (2016)",
      "url": "https://www.tableau.com/blog/tableau-online-tips-extracts-live-connections-cloud-data-53351"
    },
    {
      "title": "Tableau Exchange: Write-Back Cloud (third-party extension)",
      "url": "https://exchange.tableau.com/products/992"
    },
    {
      "title": "Redress Compliance: Tableau pricing 2026 (third party)",
      "url": "https://redresscompliance.com/tableau-pricing-2026-creator-explorer-viewer"
    },
    {
      "title": "Holistics: Tableau embedded analytics (third party)",
      "url": "https://blog.holistics.io/blog/tableau-embedded-analytics/"
    },
    {
      "title": "Atrium: Tableau Conference 2026 takeaways (third party)",
      "url": "https://atrium.ai/resources/tableau-conference-2026-key-takeaways/"
    },
    {
      "title": "Pulse.bot: Tableau agentic analytics platform news (third party)",
      "url": "https://www.pulse.bot/big-data/news/tableau-unveils-agentic-analytics-platform-adding-ai-knowledge-layer-to-bi-suite-55989c3e-f0f5-43ee-915c-a651d1ab1e15/"
    },
    {
      "title": "The Bricks: Export Tableau to Excel (third party)",
      "url": "https://www.thebricks.com/resources/guide-how-to-download-data-from-tableau-dashboard-to-excel"
    }
  ],
  "lastReviewed": "2026-11-03"
};
